import React, { ReactElement, useEffect } from 'react';

import { useDispatch } from 'react-redux';

import IconFemtasy, { iconSize } from 'components/IconFemtasy';
import { FemtasyIcon } from 'components/IconFemtasy/iconsType';
import Typography from 'components/Typography';

import {
  Speaker,
  useGetSpeakerLovedLazyQuery,
  useToggleLoveSpeakerMutation,
} from 'graphql/generated/graphql';

import useGraphaelDefaults from 'hooks/useGraphaelDefaults';
import { useTracking } from 'hooks/useTracking';

import { BannerShape, showBanner } from 'store/modules/banner';

type ReducedSpeaker = Pick<Speaker, 'id'> & {
  loved?: boolean;
};

interface IProps {
  speaker: ReducedSpeaker;
  matomoName: string;
  message?: ReactElement<typeof Typography>;
  iconSize?: iconSize;
}

const useToggleFollowSpeaker = (
  speaker: ReducedSpeaker,
  matomoName: string,
) => {
  const { trackContentInteraction } = useTracking();
  const [toggleLoveSpeaker] = useToggleLoveSpeakerMutation({
    ...useGraphaelDefaults(),
    variables: { speakerId: speaker.id },
    optimisticResponse: {
      toggleLoveSpeaker: {
        __typename: 'Speaker',
        id: speaker.id,
        loved: !speaker.loved,
      },
    },
  });
  const dispatch = useDispatch();

  return React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();

      const banner: BannerShape = {
        message: speaker.loved
          ? { id: 'components.FollowSpeakers.unfollow' }
          : { id: 'components.FollowSpeakers.follow' },
        displaySeconds: 4,
      };

      if (speaker.loved) {
        dispatch(showBanner(banner));
        trackContentInteraction('unfollow', matomoName, speaker.id);
      } else {
        dispatch(showBanner(banner));
        trackContentInteraction('follow', matomoName, speaker.id);
      }
      toggleLoveSpeaker();
    },
    [
      speaker.loved,
      speaker.id,
      toggleLoveSpeaker,
      trackContentInteraction,
      matomoName,
      dispatch,
    ],
  );
};

const FollowSpeaker = ({ speaker, message, iconSize, matomoName }: IProps) => {
  const [getSpeakerLoved, { data }] = useGetSpeakerLovedLazyQuery({
    ...useGraphaelDefaults(),
  });

  const speakerLoved = data?.speakers.speakers[0].loved || speaker.loved;

  useEffect(() => {
    if (speaker.loved === undefined && !data) {
      getSpeakerLoved({ variables: { speakerId: speaker.id.toString() } });
    }
  }, [data, getSpeakerLoved, speaker.id, speaker.loved]);

  const toggleFollowAndTracking = useToggleFollowSpeaker(
    { ...speaker, loved: speakerLoved },
    matomoName,
  );

  const followStateIcon: FemtasyIcon = speakerLoved
    ? 'Unfollowspeaker'
    : 'Followspeaker';

  return (
    <div
      className="FollowSpeaker flex items-center gap-2"
      onClick={toggleFollowAndTracking}
    >
      <IconFemtasy icon={followStateIcon} size={iconSize} />
      {message}
    </div>
  );
};

export default FollowSpeaker;
